import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Phone, Mail, MapPin, Check } from "lucide-react";
import { CONTACT, IMAGES } from "@/lib/data";
import { MaskLine } from "@/lib/motion";

const quickPoints = ["No credit checks", "Coverage included", "200 miles/day included"];
const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  // 3D tilt on the hero image
  const mvX = useMotionValue(0);
  const mvY = useMotionValue(0);
  const rotY = useSpring(mvX, { stiffness: 120, damping: 18 });
  const rotX = useSpring(mvY, { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mvX.set(((e.clientX - r.left) / r.width - 0.5) * 12);
    mvY.set((0.5 - (e.clientY - r.top) / r.height) * 12);
  };
  const onLeave = () => {
    mvX.set(0);
    mvY.set(0);
  };

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative hero-min-h flex items-center bg-gradient-to-b from-white via-secondary to-white overflow-hidden"
    >
      <div className="absolute -top-32 -right-24 w-[30rem] h-[30rem] rounded-full bg-gold/15 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[32rem] h-[32rem] rounded-full bg-navy/5 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 md:pt-32 pb-16 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left */}
        <motion.div style={{ y: contentY }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 mb-6"
          >
            <span className="text-gold-dark text-xs font-bold uppercase tracking-[0.2em]">No Credit Cards · No Problem</span>
          </motion.div>

          <h1 className="font-heading font-extrabold text-navy text-4xl sm:text-5xl lg:text-[4.1rem] leading-[1.02] tracking-tight">
            <MaskLine delay={0.1}>Ride Share Drivers</MaskLine>
            <MaskLine delay={0.22} className="text-navy">
              <span className="text-gold">&</span> Travel Nurses
            </MaskLine>
            <MaskLine delay={0.34}>Welcome</MaskLine>
          </h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.62, duration: 0.8, ease: EASE }}
            className="mt-6 h-[3px] w-28 bg-gold origin-left"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 text-lg md:text-xl text-muted-foreground font-medium"
          >
            Drive more. Worry less. We handle the rest.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 flex flex-wrap gap-x-5 gap-y-2"
          >
            {quickPoints.map((q) => (
              <span key={q} className="inline-flex items-center gap-1.5 text-navy/80 text-sm font-medium">
                <Check className="w-4 h-4 text-gold-dark" /> {q}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.68 }}
            className="mt-8 flex items-end gap-3"
          >
            <div>
              <p className="text-gold-dark text-xs font-bold uppercase tracking-[0.2em] mb-1">Starting From</p>
              <div className="flex items-baseline gap-2">
                <span className="font-heading font-extrabold text-navy text-6xl md:text-7xl leading-none">$55</span>
                <span className="text-muted-foreground font-semibold text-lg">/ day</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.76 }}
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="hero-cta-call"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-navy px-7 py-4 text-white font-bold text-base hover:bg-navy-light hover:-translate-y-1 transition-all duration-300 shadow-xl shadow-navy/20"
            >
              <Phone className="w-5 h-5" /> Call or Text {CONTACT.phoneDisplay}
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="hero-cta-email"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-navy/20 px-7 py-4 text-navy font-semibold text-base hover:border-gold hover:text-gold-dark hover:-translate-y-1 transition-all duration-300"
            >
              <Mail className="w-5 h-5" /> Email Us
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-8 flex items-center gap-2 text-muted-foreground text-sm"
          >
            <MapPin className="w-4 h-4 text-gold-dark" /> Serving {CONTACT.location}
          </motion.div>
        </motion.div>

        {/* Right image with parallax + 3D tilt */}
        <motion.div style={{ y: imgY }} className="relative [perspective:1000px]">
          <div className="absolute -inset-4 rounded-[2rem] bg-gold/20 blur-2xl" />
          <motion.div
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
            className="relative rounded-[1.75rem] overflow-hidden shadow-2xl border-4 border-white aspect-[4/5] sm:aspect-[16/12] lg:aspect-[4/5]"
          >
            <img
              src={IMAGES.heroPerson}
              alt="Happy driver with rental car"
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
            {/* spotlight */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(circle at 65% 15%, rgba(255,255,255,0.35), transparent 45%)" }}
            />
          </motion.div>

          {/* Logo signage badge */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="absolute -top-4 left-6 sm:left-8 bg-white rounded-2xl shadow-lg border border-navy/10 px-4 py-2.5"
          >
            <img src={IMAGES.logo} alt="ORL On Demand Rides logo" className="h-6 sm:h-7 w-auto" />
          </motion.div>

          {/* Floating price card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="absolute -bottom-6 -left-4 sm:-left-6 bg-white rounded-2xl shadow-xl border border-navy/10 p-4 flex items-center gap-3"
          >
            <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center shrink-0">
              <span className="font-heading font-extrabold text-gold text-lg">$55</span>
            </div>
            <div>
              <p className="font-heading font-bold text-navy leading-tight">From / day</p>
              <p className="text-muted-foreground text-xs">Coverage & maintenance included</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
